import { mergeRemoteMediaStreams } from '@playcastdotio/PeerCore';
import { playerConnectionState, mediaStreamState } from '../stores/playerStores';
import { getClientPeerManager } from './peer';

type ManagerWithSession = {
  session?: {
    connection?: {
      getRtcPeerConnection?: () => RTCPeerConnection | null;
    } | null;
  };
};

function resolveRtcPeerConnection(): RTCPeerConnection | null {
  const manager = getClientPeerManager() as ManagerWithSession | null | undefined;
  const conn = manager?.session?.connection;
  if (!conn || typeof conn.getRtcPeerConnection !== 'function') return null;
  try {
    return conn.getRtcPeerConnection() ?? null;
  } catch {
    return null;
  }
}

function restoreMediaFromReceivers(): void {
  const pc = resolveRtcPeerConnection();
  if (!pc) return;

  const receivers = pc.getReceivers();
  const stream = new MediaStream();
  for (const receiver of receivers) {
    const track = receiver.track;
    if (track && track.readyState === 'live') {
      stream.addTrack(track);
    }
  }

  if (stream.getTracks().length === 0) return;

  const merged = mergeRemoteMediaStreams(stream, mediaStreamState.currentState);
  mediaStreamState.updateState(merged);
}

/**
 * Restore the connected status after the transport recovers in place.
 *
 * An ICE restart can bring ICE back without re-firing peer-lite's `onConnected`
 * milestone when GuestPeerSession.connected stayed true (e.g. transport-lost
 * deferred to an in-flight restart). Promoting the UI store on ICE alone is too
 * early: BroadcastConnectionManager treats every 'connected' as a fresh join and
 * QuickstartCoordinator demotes playing_active → playing_afk, stopping input.
 *
 * Gate on the peer-lite connected milestone (ICE + data channel) and live video
 * after restoring receiver tracks. Only promotes from 'reconnecting'.
 */
export function restoreConnectedAfterIceRecovery(): void {
  if (playerConnectionState.currentState.status !== 'reconnecting') return;

  const manager = getClientPeerManager();
  // Align with maybeNotifyConnected(): ICE alone is not enough.
  if (!manager?.isConnected()) return;

  restoreMediaFromReceivers();

  const stream = mediaStreamState.currentState.stream;
  const hasLiveVideo =
    stream?.getVideoTracks().some((track) => track.readyState === 'live') ?? false;
  if (!hasLiveVideo) return;

  playerConnectionState.updateState({
    ...playerConnectionState.currentState,
    status: 'connected',
  });
}

/** Re-attach live receiver tracks after ICE recovery cleared the media store. */
export function restoreMediaAfterPeerLiteConnected(): void {
  restoreMediaFromReceivers();
}
